// Import all Font Awesome icons dynamically
import * as FaIcons from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { extractPercent, parsePriceLevel, calculateDollarSavingsRange } from "../../utils/couponUtils";
import BlockClubLogo from "../../media/BC.png";

function capitalizeCategory(category) {
  if (!category) return "";
  return category
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function getCategoryIcon(category, iconName = null) {
  // If iconName is provided from backend, use it dynamically
  if (iconName) {
    const IconComponent = FaIcons[iconName] || FaIcons.FaStore;
    return <IconComponent />;
  }

  // Fallback to hardcoded logic for backwards compatibility
  const categoryLower = category?.toLowerCase() || "";

  if (categoryLower === "all") return <FaIcons.FaTh />;
  if (categoryLower.includes("pet")) return <FaIcons.FaPaw />;
  if (categoryLower.includes("restaurant") || categoryLower.includes("food")) return <FaIcons.FaUtensils />;
  if (categoryLower.includes("fitness") || categoryLower.includes("gym")) return <FaIcons.FaDumbbell />;
  if (categoryLower.includes("hair") || categoryLower.includes("salon") || categoryLower.includes("barber")) return <FaIcons.FaCut />;

  return <FaIcons.FaStore />;
}

function VenueCard({
  company,
  handleViewDeals,
  venueOffers,
  couponCount = null
}) {
  const { t } = useTranslation();
  const subCategory = company.sub_category || company.category || "Restaurant";
  const offers = venueOffers[company.venue_id] || [];
  const minExpirationDays = company.min_expiration_days;

  // Determine logo source - use BC logo directly if venue has no logo
  const hasVenueLogo = Boolean(company.poster_url || company.logo_url);
  const logoSrc = hasVenueLogo
    ? (company.poster_url || company.logo_url)
    : BlockClubLogo;

  const [imageError, setImageError] = useState(false);
  const [fallbackSrc, setFallbackSrc] = useState(null);

  // Get the first offer to display
  const firstOffer = offers.length > 0 ? offers[0] : null;

  // Calculate savings for percent-based offers
  let offerDisplay = null;
  if (firstOffer) {
    const percent = extractPercent(firstOffer.title);
    if (percent) {
      // For percent-off deals, calculate dollar savings
      const priceRange = parsePriceLevel(company.price_text);
      const savingsRange = calculateDollarSavingsRange(percent, priceRange);
      offerDisplay = {
        type: 'percent',
        percent,
        savingsRange,
        title: firstOffer.title
      };
    } else {
      // For other offers, just show the title
      offerDisplay = {
        type: 'other',
        title: firstOffer.title
      };
    }
  }

  const handleCardClick = () => {
    handleViewDeals(company.venue_id);
  };

  // Show full address with ellipsis overflow handling in CSS
  const addressParts = [
    company.address_1,
    company.address_2,
    company.city,
    company.state,
    company.postal_code,
  ].filter(Boolean);
  const fullAddress = addressParts.join(", ");

  return (
    <div
      className="perks-company-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Coming Soon Badge - Show for logged-in users with no coupons and no active offers */}
      {couponCount !== null && couponCount === 0 && (
        <div className="venue-coming-soon-badge">
          Discounts coming soon
        </div>
      )}

      <div className="perks-company-image-section">
        <img
          src={fallbackSrc || logoSrc}
          alt={`${company.venue_name} ${company.poster_url ? 'poster' : 'logo'}`}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // Only trigger fallback if venue has a logo that failed to load
            if (!imageError && hasVenueLogo && !fallbackSrc) {
              setImageError(true);
              setFallbackSrc(BlockClubLogo);
            }
          }}
        />
        <div className="perks-company-tags">
          {subCategory && (
            <div className="perks-company-category">
              {getCategoryIcon(subCategory, company.category_icon)}
              <span>{t(`categories.${capitalizeCategory(subCategory)}`, capitalizeCategory(subCategory))}</span>
            </div>
          )}
        </div>
        {/* Expiration badge - Show in top right corner for urgent expiration */}
        {couponCount !== null && minExpirationDays !== null && minExpirationDays !== undefined && minExpirationDays <= 10 && (
          <div className="venue-expiration-badge">
            {minExpirationDays <= 3 ? (
              <span className="urgent">Expires in {minExpirationDays}d</span>
            ) : (
              <span>{minExpirationDays}d left</span>
            )}
          </div>
        )}
      </div>

      <div className="perks-company-info">
        <div className="perks-company-content">
          <h3 className="perks-company-name">
            {company.venue_name}
          </h3>

          {fullAddress && (
            <div className="perks-company-detail perks-company-address">
              <FaIcons.FaMapMarkerAlt />
              <span>{fullAddress}</span>
            </div>
          )}

          <div className="perks-company-meta">
            {company.rating && (
              <div className="perks-company-rating">
                <FaIcons.FaStar />
                <span>{company.rating.toFixed(1)}</span>
                {company.rating_count > 0 && (
                  <span className="rating-count">({company.rating_count})</span>
                )}
              </div>
            )}
            {company.rating && (company.distance !== undefined && company.distance !== null) && (
              <span className="meta-separator">•</span>
            )}
            {(company.distance !== undefined && company.distance !== null) && (
              <div className="perks-company-distance">
                <span>{company.distance} mi</span>
              </div>
            )}
          </div>
        </div>

        {/* Deals Preview - Groupon Style */}
        {offerDisplay && (
          <div className="perks-deals-preview">
            <div className="deal-highlight">
              <div className="deal-savings-main">
                <FaIcons.FaBolt />
                {offerDisplay.type === 'percent' ? (
                  offerDisplay.savingsRange.high >= 4 ? (
                    <div>
                      <span className="savings-dollar">
                        {offerDisplay.savingsRange.low === offerDisplay.savingsRange.high
                          ? `Save ~$${offerDisplay.savingsRange.low}`
                          : `Save ~$${offerDisplay.savingsRange.low}-${offerDisplay.savingsRange.high}`}
                      </span>
                      <span className="savings-percent"> ({offerDisplay.percent}% off)</span>
                    </div>
                  ) : (
                    <span className="savings-dollar">Save {offerDisplay.percent}% off on your visit</span>
                  )
                ) : (
                  <span className="savings-dollar">{offerDisplay.title}</span>
                )}
              </div>
            </div>
            {/* CTA section with badge and button */}
            <div className="perks-card-cta-wrapper">
              {/* Coupon Count Badge - Show for logged-in users with coupons */}
              {(couponCount !== null && couponCount > 0) ?
              (
                <div className="venue-coupon-badge-inline clickable">
                  {couponCount} {couponCount === 1 ? 'Coupon' : 'Coupons'} - View Offers
                </div>
              ) : (
                <span>View Offers</span>
              )}
            </div>
          </div>
        )}

        {/* Show "Coming soon" message and button for logged-in users with no coupons */}
        {couponCount !== null && couponCount === 0 && !offerDisplay && (
          <div className="perks-deals-preview">
            <div className="coming-soon-message">
              Discounts coming soon
            </div>
            {/* CTA section with button */}
            <div className="perks-card-cta-wrapper">
              <div className="perks-card-cta">
                <span>View venue</span>
              </div>
            </div>
          </div>
        )}

        {/* Fallback "View" button for venues without any deals preview section */}
        {!offerDisplay && (couponCount === null || couponCount === 0) && (
          <div className="perks-deals-preview">
            <div className="perks-card-cta-wrapper">
              <div className="perks-card-cta">
                <span>View venue</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Export helper functions for use in other components
export { capitalizeCategory, getCategoryIcon };

export default VenueCard;
